/*
 * Save queue + outbound mutation runner. See IMPLEMENTATION_PLAN.md
 * §4 "Saving — robustness model".
 *
 * Three layers:
 *  1) Per-row write hits the backend immediately.
 *  2) Optimistic UI applies the change first; revert on 4xx.
 *  3) On 5xx / network error, mutation is parked in IDB and retried on
 *     reconnect or on next mount.
 */

import { create } from 'zustand';
import { openDB, type IDBPDatabase } from 'idb';
import type { QueuedMutation, SaveStatus } from '@/types';

const DB_NAME = 'studio-v2';
const DB_VERSION = 1;
const STORE = 'mutations';

let dbPromise: Promise<IDBPDatabase> | null = null;
function db(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE)) {
          const store = database.createObjectStore(STORE, { keyPath: 'id' });
          store.createIndex('createdAt', 'createdAt');
          store.createIndex('episodeUuid', 'episodeUuid');
        }
      },
    });
  }
  return dbPromise;
}

interface SaveQueueStore {
  status: SaveStatus;
  queueCount: number;
  inFlightCount: number;
  setStatus: (status: SaveStatus) => void;
  setQueueCount: (n: number) => void;
  bumpInFlight: (delta: 1 | -1) => void;
}

export const useSaveQueueStore = create<SaveQueueStore>((set) => ({
  status: { kind: 'idle', lastSavedAt: null },
  queueCount: 0,
  inFlightCount: 0,
  setStatus: (status) => set({ status }),
  setQueueCount: (queueCount) => set({ queueCount }),
  bumpInFlight: (delta) =>
    set((s) => ({ inFlightCount: Math.max(0, s.inFlightCount + delta) })),
}));

export async function enqueue(mutation: QueuedMutation): Promise<void> {
  const database = await db();
  await database.put(STORE, mutation);
  await refreshCount();
}

export async function dequeue(id: string): Promise<void> {
  const database = await db();
  await database.delete(STORE, id);
  await refreshCount();
}

export async function listQueued(episodeUuid?: string): Promise<QueuedMutation[]> {
  const database = await db();
  if (episodeUuid) {
    const idx = database.transaction(STORE).store.index('episodeUuid');
    return idx.getAll(episodeUuid);
  }
  return database.getAll(STORE);
}

export async function refreshCount(): Promise<void> {
  const database = await db();
  const count = await database.count(STORE);
  useSaveQueueStore.getState().setQueueCount(count);
  recomputeStatus();
}

function recomputeStatus(): void {
  const { inFlightCount, queueCount } = useSaveQueueStore.getState();
  if (queueCount > 0 && navigator.onLine === false) {
    useSaveQueueStore.getState().setStatus({ kind: 'queued', count: queueCount });
  } else if (inFlightCount > 0) {
    useSaveQueueStore.getState().setStatus({ kind: 'saving', since: Date.now() });
  } else if (queueCount === 0) {
    useSaveQueueStore.getState().setStatus({ kind: 'idle', lastSavedAt: Date.now() });
  }
}

export async function initSaveQueue(): Promise<void> {
  await refreshCount();
  window.addEventListener('online', () => {
    void refreshCount();
  });
  window.addEventListener('offline', () => {
    void refreshCount();
  });
}

export function newMutationId(): string {
  return crypto.randomUUID();
}

/**
 * Run a mutation through the three-layer model. Caller passes in an `apply`
 * function that performs the actual fetch, plus the metadata needed to queue
 * the mutation if it fails transiently. Returns the apply function's result.
 *
 * On 4xx / explicit "Bad Request"-class errors → throws, caller reverts UI.
 * On 5xx / network → parks the mutation in IDB and rethrows; caller keeps
 * optimistic state but knows the write isn't committed yet.
 */
export async function runMutation<T>(
  meta: Omit<QueuedMutation, 'id' | 'createdAt' | 'retryCount'>,
  apply: () => Promise<T>,
): Promise<T> {
  useSaveQueueStore.getState().bumpInFlight(1);
  recomputeStatus();
  try {
    const result = await apply();
    return result;
  } catch (err) {
    if (isTransient(err)) {
      const mutation: QueuedMutation = {
        ...meta,
        id: newMutationId(),
        createdAt: Date.now(),
        retryCount: 0,
      };
      await enqueue(mutation);
    } else {
      useSaveQueueStore
        .getState()
        .setStatus({ kind: 'error', message: errorMessage(err) });
    }
    throw err;
  } finally {
    useSaveQueueStore.getState().bumpInFlight(-1);
    recomputeStatus();
  }
}

function isTransient(err: unknown): boolean {
  if (err instanceof TypeError) return true; // fetch network failure
  if (err && typeof err === 'object' && 'status' in err) {
    const status = (err as { status: number }).status;
    return status >= 500;
  }
  return false;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
